import type { IncomingHttpHeaders } from 'http';

export type WafTrafficMetadata = {
  waf_bot_name: string | null;
  waf_country: string | null;
  waf_suspicious_traffic: boolean;
  waf_suspicious_traffic_test: boolean;
};

export const EMPTY_WAF_TRAFFIC_METADATA: WafTrafficMetadata = {
  waf_bot_name: null,
  waf_country: null,
  waf_suspicious_traffic: false,
  waf_suspicious_traffic_test: false,
};

type HeaderValue = string | string[] | undefined;
type HeaderParser = (value: HeaderValue) => boolean | string | null;

const parseBooleanHeader = (value: HeaderValue): boolean =>
  Array.isArray(value)
    ? value.some((item) => item.trim().toLowerCase() === 'true')
    : value?.trim().toLowerCase() === 'true';

const parseStringHeader = (value: HeaderValue): string | null => {
  const firstValue = Array.isArray(value) ? value[0] : value;

  return firstValue?.trim() || null;
};

const WAF_HEADER_CONFIG: Record<keyof WafTrafficMetadata, { name: string; parse: HeaderParser }> = {
  waf_bot_name: { name: 'x-amzn-waf-bot-name', parse: parseStringHeader },
  waf_country: { name: 'x-amzn-waf-country', parse: parseStringHeader },
  waf_suspicious_traffic: {
    name: 'x-amzn-waf-suspicious-traffic',
    parse: parseBooleanHeader,
  },
  waf_suspicious_traffic_test: {
    name: 'x-amzn-waf-suspicious-traffic-test',
    parse: parseBooleanHeader,
  },
};

const getHeaderValue = (headers: IncomingHttpHeaders | undefined, name: string): HeaderValue =>
  headers?.[name];

/**
 * Reads the WAF headers from the SSR request for analytics. The values are
 * logging-only; they do not block the request or initialize Segment
 * differently.
 */
export const getWafTrafficMetadata = (headers?: IncomingHttpHeaders): WafTrafficMetadata => {
  const metadata = Object.fromEntries(
    Object.entries(WAF_HEADER_CONFIG).map(([property, config]) => [
      property,
      config.parse(getHeaderValue(headers, config.name)),
    ])
  ) as WafTrafficMetadata;

  return metadata;
};

export const mergeWafTrafficMetadata = (
  current: WafTrafficMetadata,
  next: WafTrafficMetadata
): WafTrafficMetadata =>
  Object.fromEntries(
    Object.keys(current).map((property) => [
      property,
      current[property as keyof WafTrafficMetadata] || next[property as keyof WafTrafficMetadata],
    ])
  ) as WafTrafficMetadata;
